import api from '../../../../services/api';
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PageContainer from '../../../../common/components/PageContainer';
import useAlerta from '../../../../common/components/useAlerta';
import AppSnackbar from '../../../../common/components/AppSnackbar';

function CadastroOrcamento() {
  const { register, handleSubmit, reset, getValues } = useForm();
  const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const [fornecedor, setFornecedor] = useState("");
  const [itens, setItens] = useState([]);
  const [itensEditaveis, setItensEditaveis] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [showNewFornecedor, setShowNewFornecedor] = useState(false);

  const handleNovoFornecedor = async () => {
    try {
      const novoForn = getValues("novoFornecedor");
      await api.post("/sc/fornecedor", novoForn);
      fetchFornecedores();
      setShowNewFornecedor(false);
      mostrarAlerta("Fornecedor cadastrado com sucesso!", "success");
      reset();
      setTimeout(fecharAlerta, 1500);
    } catch (error) {
      console.error(error);
      mostrarAlerta("Erro ao cadastrar fornecedor!", "error");
    }
  };

  const fetchFornecedores = async () => {
    try {
      const response = await api.get("/sc/fornecedor");
      setFornecedores(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao carregar fornecedores:", error);
      setFornecedores([]);
    }
  };

  const fetchItens = async () => {
    try {
      const response = await api.get(`/sc/${id}/itens`);
      setItens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao carregar itens:", error);
      setItens([]);
    }
  };

  useEffect(() => {
    fetchFornecedores();
    fetchItens();
  }, [id]);

  useEffect(() => {
    setItensEditaveis(
      itens.map((item) => ({
        ...item,
        valorUnitario: item.valorUnitario ? parseFloat(item.valorUnitario) : 0
      }))
    );
  }, [itens]);

  const handleChangeValorUnitario = (index, novoValor) => {
    const novosItens = [...itensEditaveis];
    novosItens[index].valorUnitario = parseFloat(novoValor) || 0;
    novosItens[index].valorTotal = novosItens[index].quantidade * novosItens[index].valorUnitario;
    setItensEditaveis(novosItens);
  };

  const totalOrcamento = itensEditaveis.reduce(
    (acc, item) => acc + (item.quantidade * item.valorUnitario),
    0
  );

  const onSubmit = async (data) => {
    const formData = {
      ...data,
      valor: totalOrcamento,
      idFornecedor: fornecedor,
      idSC: id,
      itens: itensEditaveis
    };

    try {
      await api.post(`/sc/${id}/orcamento`, formData);
      mostrarAlerta("Orçamento cadastrado com sucesso!", "success");
      reset();
      setTimeout(() => {
        window.location.href = `/sc/${id}/orcamento`;
      }, 1500);
    } catch (error) {
      console.error(error);
      mostrarAlerta("Erro ao cadastrar orçamento!", "error");
    }
  };

  return (
    <PageContainer sx={{ maxWidth: 900 }}>
      <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
          Orçamento
        </Typography>

        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Descrição</TableCell>
                <TableCell>UN</TableCell>
                <TableCell>Qtd</TableCell>
                <TableCell>Valor Unitário</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itensEditaveis.map((item, index) => (
                <TableRow key={item.id || index}>
                  <TableCell>{item.descricao}</TableCell>
                  <TableCell>{item.unidadeMedida}</TableCell>
                  <TableCell>{item.quantidade}</TableCell>
                  <TableCell sx={{ width: 140 }}>
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ step: '0.01' }}
                      onChange={(e) => handleChangeValorUnitario(index, parseFloat(e.target.value) || 0)}
                      required
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="right">
                    {(item.quantidade * item.valorUnitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField {...register("prazoEntrega")} label="Prazo de Entrega (dias)" type="number" required fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Valor total do orçamento"
              value={totalOrcamento.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Fornecedor"
              defaultValue=""
              onChange={(e) => setFornecedor(e.target.value)}
              required
              fullWidth
            >
              <MenuItem value="">Selecione um fornecedor</MenuItem>
              {fornecedores.map((forn) => (
                <MenuItem key={forn.idFornecedor} value={forn.idFornecedor}>
                  {forn.nome + " | " + forn.rua + ", " + forn.numero + ", " + forn.bairro + ", " + forn.cidade + " - " + forn.estado}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setShowNewFornecedor(true)}>
          Cadastrar novo fornecedor
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button type="submit" variant="contained" color="secondary" size="large" sx={{ px: 6 }}>
            Cadastrar orçamento
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={showNewFornecedor}
        onClose={() => setShowNewFornecedor(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>Novo Fornecedor</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField {...register("novoFornecedor.nome")} label="Nome" fullWidth />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField {...register("novoFornecedor.cpf")} label="CPF" fullWidth />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField {...register("novoFornecedor.cnpj")} label="CNPJ" fullWidth />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField {...register("novoFornecedor.rua")} label="Rua" fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField {...register("novoFornecedor.numero")} label="Número" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField {...register("novoFornecedor.bairro")} label="Bairro" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField {...register("novoFornecedor.cidade")} label="Cidade" fullWidth />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField {...register("novoFornecedor.estado")} label="Estado (Ex: PB)" fullWidth />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField {...register("novoFornecedor.cep")} label="CEP" fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField {...register("novoFornecedor.telefone")} label="Telefone" fullWidth />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewFornecedor(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleNovoFornecedor} variant="contained" color="secondary">Salvar</Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
    </PageContainer>
  );
}

export default CadastroOrcamento;
